import React, { useCallback, useRef } from 'react';
import { Autocomplete, useLoadScript } from '@react-google-maps/api';
import { Input } from 'antd';
import type { InputProps } from 'antd';
import type { ParsedGooglePlace } from '../utils/addressUtils';
import { parseGooglePlace } from '../utils/addressUtils';

const libraries: 'places'[] = ['places'];

export interface AddressAutocompleteInputProps extends Omit<InputProps, 'onChange'> {
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** Fired when user picks a Places suggestion (in addition to updating the address line). */
  onPlaceResolved?: (place: ParsedGooglePlace) => void;
}

/**
 * Uses Google's native Places Autocomplete (same dropdown + "Powered by Google" branding as maps.google.com).
 * ZIP/postal appears when Google's predictions include it in each row; after selection we still fill fields from Place Details.
 */
const InnerAutocomplete: React.FC<AddressAutocompleteInputProps> = ({
  onPlaceResolved,
  onChange,
  value,
  ...inputProps
}) => {
  const acRef = useRef<any>(null);

  const onLoad = useCallback((autocomplete: any) => {
    acRef.current = autocomplete;
    autocomplete.setFields(['address_components', 'formatted_address', 'geometry', 'name']);
  }, []);

  const onUnmount = useCallback(() => {
    acRef.current = null;
  }, []);

  const onPlaceChanged = useCallback(() => {
    const place = acRef.current?.getPlace();
    if (!place || !place.address_components?.length) return;
    const parsed = parseGooglePlace(place);
    onPlaceResolved?.(parsed);
    const synthetic = {
      target: { value: parsed.streetLine },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange?.(synthetic);
  }, [onChange, onPlaceResolved]);

  return (
    <Autocomplete
      onLoad={onLoad}
      onUnmount={onUnmount}
      onPlaceChanged={onPlaceChanged}
      options={{
        types: ['address'],
      }}
    >
      <Input {...inputProps} value={value} onChange={onChange} />
    </Autocomplete>
  );
};

/**
 * Street address with Google Places when `REACT_APP_GOOGLE_MAPS_API_KEY` is set.
 * Otherwise a plain Ant Design Input (manual entry unchanged).
 */
const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = (props) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return <Input {...props} />;
  }
  return <AddressAutocompleteWithScript {...props} />;
};

const AddressAutocompleteWithScript: React.FC<AddressAutocompleteInputProps> = (props) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  if (loadError) {
    return <Input {...props} placeholder={props.placeholder || 'Enter street address'} />;
  }
  if (!isLoaded) {
    return <Input {...props} disabled placeholder="Loading address search…" />;
  }
  return <InnerAutocomplete {...props} />;
};

export default AddressAutocompleteInput;
